/**
 * 统一认证会话：从 Cookie 恢复 Vuex、判断登录态、清理并跳转登录页
 * 注意：会话过期跳转时保留 REMEMBER_ME（7 天免登录），仅用户主动退出时清除
 */
import store from "@/store";
import router from "@/router";
import VueCookies from "vue-cookies";
import { isMobile } from "@/utils/helpers";
import { hydrateRememberMeFromCookie } from "@/utils/rememberMe";

export const AUTH_MESSAGE_KEYWORDS = [
	"没有鉴权",
	"没有登录",
	"未登录",
	"未授权",
	"请重新登录",
	"登录失效",
	"登录过期",
	"token无效",
	"token失效",
	"会话过期",
	"会话失效",
];

let isRedirectingToLogin = false;

export function readCookie(name) {
	const escaped = name.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
	const match = document.cookie.match(
		new RegExp(`(?:^|;\\s*)${escaped}=([^;]*)`)
	);
	return match ? decodeURIComponent(match[1]) : "";
}

export function parseUserCookie(raw) {
	if (!raw) {
		return null;
	}
	try {
		return JSON.parse(raw);
	} catch (e) {
		return null;
	}
}

function isValidUserObject(user) {
	return !!(user && (user.displayName || user.username));
}

/** 7 天免登录 Cookie 是否有效 */
export function hasValidRememberMe(vueCookies = VueCookies) {
	if (!vueCookies || typeof vueCookies.get !== "function") {
		return false;
	}
	const obj = vueCookies.get("REMEMBER_ME");
	return !!(obj && obj.token && isValidUserObject(obj.user));
}

/** 是否有可用的用户信息（含 7 天免登录 REMEMBER_ME） */
export function hasValidUserContext(vueCookies = VueCookies) {
	const user = store.getters["auth/user"];
	if (isValidUserObject(user)) {
		return true;
	}
	const fromCookie = parseUserCookie(readCookie("user"));
	if (isValidUserObject(fromCookie)) {
		return true;
	}
	if (hasValidRememberMe(vueCookies)) {
		const obj = vueCookies.get("REMEMBER_ME");
		return isValidUserObject(obj.user);
	}
	return false;
}

/**
 * 尝试用 7 天免登录恢复会话（congress / token / user / Vuex）
 * @returns {boolean} 是否恢复成功
 */
export function tryRestoreRememberMeSession(vueCookies = VueCookies) {
	if (!hasValidRememberMe(vueCookies)) {
		return false;
	}
	hydrateRememberMeFromCookie(store, vueCookies);
	return hasValidUserContext(vueCookies);
}

/**
 * 将 congress / user / token Cookie 同步到 Vuex（非 7 天免登录场景）
 * 已勾选免登录时由 rememberMe.js 统一写入，避免用失效 congress 覆盖有效 token
 */
export function hydrateAuthFromCookies(vueCookies = VueCookies) {
	if (hasValidRememberMe(vueCookies)) {
		return;
	}
	const congress = readCookie("congress");
	const tokenCookie = readCookie("token");
	const token = congress || tokenCookie;
	if (token) {
		store.commit("auth/setToken", token);
		store.commit("auth/setSessionToken", token);
	}
	const user = parseUserCookie(readCookie("user"));
	if (isValidUserObject(user)) {
		store.commit("auth/setUser", user);
		store.commit("auth/setSessionUser", user);
	}
}

export function isAuthErrorMessage(message) {
	const msg = String(message || "");
	return AUTH_MESSAGE_KEYWORDS.some((keyword) => msg.includes(keyword));
}

export function getLoginPath() {
	return isMobile() ? "/mlogin" : "/authserver/login";
}

/** 仅清理会话 Cookie，不触碰 REMEMBER_ME */
export function clearSessionAuthCookies() {
	["congress", "token", "user"].forEach((name) => {
		document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
		const host = window.location.hostname || "";
		const parts = host.split(".");
		if (parts.length > 2) {
			const domain = `.${parts.slice(-2).join(".")}`;
			document.cookie = `${name}=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
		}
	});
	store.commit("auth/setUser", {});
	store.commit("auth/setToken", "");
	localStorage.removeItem("token");
	localStorage.removeItem("user");
	sessionStorage.removeItem("token");
	sessionStorage.removeItem("user");
}

/**
 * 清理本地会话并跳转登录页
 * @param {{ preserveRememberMe?: boolean }} options
 *   preserveRememberMe 默认 true：保留 7 天免登录，仅清当前会话
 */
export function clearAuthStateAndRedirect(options = {}) {
	const { preserveRememberMe = true } = options;
	const loginPath = getLoginPath();
	if (isRedirectingToLogin || router.currentRoute.path === loginPath) {
		return;
	}
	isRedirectingToLogin = true;

	let rememberBackup = null;
	if (
		preserveRememberMe &&
		VueCookies &&
		typeof VueCookies.get === "function" &&
		hasValidRememberMe(VueCookies)
	) {
		rememberBackup = VueCookies.get("REMEMBER_ME");
	}

	store.commit("auth/setUser", {});
	store.commit("auth/setToken", "");
	const language = localStorage.getItem("language");
	localStorage.clear();
	if (language) {
		localStorage.setItem("language", language);
	}
	sessionStorage.clear();

	if (VueCookies && typeof VueCookies.keys === "function") {
		VueCookies.keys().forEach((el) => {
			if (preserveRememberMe && el === "REMEMBER_ME") {
				return;
			}
			VueCookies.remove(el);
		});
	}

	clearSessionAuthCookies();

	if (rememberBackup && VueCookies && typeof VueCookies.set === "function") {
		VueCookies.set("REMEMBER_ME", rememberBackup, "7d");
	}

	router.replace({ path: loginPath }).finally(() => {
		isRedirectingToLogin = false;
	});
}

/** congress 在但无用户信息：先尝试 7 天免登录，失败再清会话（不清 REMEMBER_ME） */
export function clearStaleAuthCookies(vueCookies = VueCookies) {
	if (tryRestoreRememberMeSession(vueCookies)) {
		return;
	}
	clearSessionAuthCookies();
}

/** 是否具备登录态（含 7 天免登录） */
export function hasActiveLoginSession(vueCookies = VueCookies) {
	if (hasValidRememberMe(vueCookies)) {
		tryRestoreRememberMeSession(vueCookies);
	}
	hydrateAuthFromCookies(vueCookies);
	const hasCongress = !!readCookie("congress");
	return (hasCongress || hasValidRememberMe(vueCookies)) && hasValidUserContext(vueCookies);
}
